import Button from "@syntensor/common/components/button";
import getCopy from "@syntensor/common/data/copy";

import styles from "./create.module.css";
import { navigateTo } from "@syntensor/common/browser_history";
import Input from "@syntensor/common/components/input";
import { useEffect, useState, useRef } from "react";
import Table, {
  deleteCellRenderer,
  inputCellRenderer,
  TableRef,
} from "@syntensor/common/components/table";
import Modal from "react-modal";
import { useRouteMatch } from "react-router-dom";
import {
  fetchCreateGroup,
  fetchGroup,
  fetchGroupData,
  fetchUsers,
} from "@syntensor/common/data/fetch_data";
import { CreateGroupPayload, ICognitoUser } from "@syntensor/common/types";
import { validEmail } from "../../studies/create/compounds/tableConfig";
import Loader from "@syntensor/common/components/loader";

export interface IGroupMatchParams {
  0: string;
  groupId?: string;
  cloneId?: string;
}

export default function CreateNewGroupForm() {
  const { params } = useRouteMatch<IGroupMatchParams>();
  const { groupId, cloneId } = params;

  const [isLoading, setIsLoading] = useState(false);
  const [showGroupAdminModal, setGroupAdminModal] = useState(false);
  const groupAdminModalTableRef = useRef<TableRef>(null);
  const groupAdminTableRef = useRef<TableRef>(null);

  const [showGroupMembersModal, setGroupMembersModal] = useState(false);
  const groupMembersModalTableRef = useRef<TableRef>(null);
  const groupMembersTableRef = useRef<TableRef>(null);

  const [name, setName] = useState<string>("");
  const [groupAdmins, setGroupAdmins] = useState<Record<string, any>[]>([]);
  const [groupProjects, setGroupProjects] = useState<Record<string, any>[]>([]);
  const [groupMembers, setGroupMembers] = useState<Record<string, any>[]>([]);
  const backUrl = "/groups/";

  const [cognitoUsersCache, setCognitoUsersCache] = useState<ICognitoUser[]>(
    []
  );

  async function getMatch(email: string) {
    let cognitoUsers = cognitoUsersCache;
    if (cognitoUsersCache.length === 0) {
      cognitoUsers = await fetchUsers();
      setCognitoUsersCache(cognitoUsers);
    }
    return cognitoUsers.find(
      (user: ICognitoUser) => user.email.toLowerCase() === email.toLowerCase()
    );
  }

  const groupAdminTableConfig = {
    cols: [
      {
        id: "name",
        name: "Name",
        width: "33%",
      },
      {
        id: "email",
        name: "Email",
        width: "66%",
        inputCellRenderer: inputCellRenderer,
        validateHandler: async function (
          datum: Record<string, any>
        ): Promise<boolean> {
          const input = datum[this.id!];
          if (!validEmail(input)) {
            datum.name = "";
            datum.sub = "";
            return true;
          }

          const match = await getMatch(input);
          let invalid = true;

          if (match) {
            datum["name"] = `${match.given_name} ${match.family_name}`;
            datum["sub"] = match.sub;
            invalid = false;
          } else {
            datum.name = "";
            datum.sub = "";
          }

          return invalid;
        },
      },
    ],
  };

  const groupMembersTableConfig = {
    cols: [
      {
        id: "name",
        name: "Name",
        width: "33%",
      },
      {
        id: "email",
        name: "Email",
        width: "33%",
        inputCellRenderer: inputCellRenderer,
        validateHandler: async function (
          datum: Record<string, any>
        ): Promise<boolean> {
          const input = datum[this.id!];
          if (!validEmail(input)) {
            datum.name = "";
            datum.sub = "";
            datum.status = "";
            return true;
          }

          const match = await getMatch(input);
          let invalid = true;

          if (match) {
            datum["name"] = `${match.given_name} ${match.family_name}`;
            datum["sub"] = match.sub;
            datum["status"] = match.status;
            invalid = false;
          } else {
            datum.name = "";
            datum.sub = "";
            datum.status = "";
          }

          return invalid;
        },
      },
      {
        id: "status",
        name: "Status",
        width: "33%",
      },
    ],
  };

  useEffect(() => {
    async function asyncFetch() {
      if (groupId) {
        try {
          setIsLoading(true);
          const groupData = await fetchGroupData(groupId);
          const group = groupData.group;

          console.dir(group);

          setName(group[0]?.name ?? "");
          setGroupAdmins(
            group
              .filter(
                // admin role
                (g: Record<string, any>) => g.roleId === 2 || g.roleId === 1
              )
              .map((g: Record<string, any>) => ({
                name: g.given_name + " " + g.family_name,
                email: g.email,
                userId: g.userId,
                sub: g.awsSub,
              }))
          );

          setGroupMembers(
            group
              .filter((g: Record<string, any>) => g.roleId === 3) // member role
              .map((g: Record<string, any>) => ({
                name: g.given_name + " " + g.family_name,
                email: g.email,
                status: g.status,
                userId: g.userId,
                sub: g.awsSub,
              }))
          );

          const projects = groupData.projects.map((p: Record<string, any>) => ({
            name: p.name,
            description: p.description.substring(0, 50) + "...",
          }));

          setGroupProjects(projects);

          setIsLoading(false);
        } catch (err) {
          console.error(err);
        }
      }

      // this ID isn't used when saving
      if (cloneId) {
        try {
          setIsLoading(true);
          const group = await fetchGroup(cloneId);

          setName(`${group.name ?? ""} clone`);

          setIsLoading(false);
        } catch (err) {
          console.error(err);
        }
      }
    }

    asyncFetch();
  }, []);

  const back = () => {
    navigateTo(backUrl);
  };

  if (isLoading) {
    return <Loader />;
  }
  // if (templateLoadError) {
  //   return <ErrorComp error={templateLoadError} />;
  // }

  async function onSubmit() {
    const payload: CreateGroupPayload = {
      name: name,
      id: groupId ? parseInt(groupId) : -1,
      groupAdmins: groupAdmins,
      groupMembers: groupMembers,
    };

    await fetchCreateGroup(payload);

    navigateTo(backUrl);
  }

  function saveChanges() {
    if (groupAdminModalTableRef.current) {
      const users = groupAdminModalTableRef.current.getInternalState();

      let valid = true;
      for (let i = 0; i < users.length; i++) {
        if (users[i].invalidemail) {
          valid = false;
          i = users.length;
        }
      }

      if (!valid) return;

      const emails = users.map((user) => user.email);
      const hasDuplicates = emails.length !== new Set(emails).size;
      if (hasDuplicates) return;

      setGroupAdmins(users);

      if (groupAdminTableRef.current) {
        groupAdminTableRef.current.setInternalState(users);
      }

      setGroupAdminModal(false);
    }

    if (groupMembersModalTableRef.current) {
      const users = groupMembersModalTableRef.current.getInternalState();

      let valid = true;
      for (let i = 0; i < users.length; i++) {
        if (users[i].invalidemail) {
          valid = false;
          i = users.length;
        }
      }

      if (!valid) return;

      const emails = users.map((user) => user.email);
      const hasDuplicates = emails.length !== new Set(emails).size;
      if (hasDuplicates) return;

      setGroupMembers(users);

      if (groupMembersTableRef.current) {
        groupMembersTableRef.current.setInternalState(users);
      }

      setGroupMembersModal(false);
    }
  }

  return (
    <div id="root" className={styles.newUserForm}>
      <form onSubmit={onSubmit}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>{`Configure ${name}`}</h2>

          <div className={styles.buttonsWrapper}>
            <Button role="subtle" onClick={back}>
              {getCopy("studies_search_discard-changes")}
            </Button>
          </div>
        </div>

        <div className={styles.content}>
          <div>
            <h3 className={styles.formTitle}>Group Name</h3>

            <Input
              size="small"
              placeholder={getCopy("groups_create_name-placeholder")}
              name="name"
              defaultValue={name}
              onChange={(e: any) => {
                setName(e.target.value);
              }}
            />
          </div>
        </div>

        <div className={styles.content}>
          <div style={{ marginBottom: "1em" }}>
            <h3 className={styles.formTitle}>Group Admins</h3>

            <Table
              ref={groupAdminTableRef}
              config={groupAdminTableConfig}
              data={groupAdmins}
            />
          </div>

          {/* <div>
            <Button
              onClick={() => {
                setGroupAdminModal(true);
              }}
              role="secondary"
            >
              {getCopy("groups_group_edit-admins")}
            </Button>
          </div> */}
        </div>

        <div className={styles.content}>
          <div style={{ marginBottom: "1em" }}>
            <h3 className={styles.formTitle}>Projects Overview</h3>

            <Table
              config={{
                cols: [
                  {
                    id: "name",
                    name: "Name",
                    width: "33%",
                  },
                  {
                    id: "description",
                    name: "Description",
                    width: "33%",
                  },
                ],
              }}
              data={groupProjects}
            />
          </div>

          <div className={styles.compoundFormSection}>
            <Button
              className={styles.addCompoundBtn}
              isDisabled={!groupId}
              onClick={() => {
                navigateTo(`/groups/${groupId}`);
              }}
              role="secondary"
            >
              {getCopy("groups_group_edit-projects")}
            </Button>
          </div>
        </div>

        <div className={styles.content}>
          <div style={{ marginBottom: "1em" }}>
            <h3 className={styles.formTitle}>Group Members</h3>

            <Table
              ref={groupMembersTableRef}
              config={groupMembersTableConfig}
              data={groupMembers}
            />
          </div>

          {/* <div className={styles.compoundFormSection}>
            <Button
              className={styles.addCompoundBtn}
              onClick={() => {
                setGroupMembersModal(true);
              }}
              role="secondary"
            >
              {getCopy("groups_group_edit-members")}
            </Button>
          </div> */}
        </div>

        <div className={styles.content}>
          <div style={{ paddingBottom: "2em" }}>
            <Button
              onClick={onSubmit}
              isDisabled={name.length === 0}
              name="savebutton"
              role="primary"
            >
              {getCopy("groups_group_save")}
            </Button>
          </div>
        </div>

        <Modal
          isOpen={showGroupAdminModal}
          onRequestClose={() => setGroupAdminModal(false)}
          contentLabel="Group Admin Users"
          style={{
            overlay: {
              backgroundColor: "var(--primary-light)",
              zIndex: 99999,
            },
            content: {
              backgroundColor: "var(--primary-light)",
              position: "absolute",
              top: "0%",
              left: "0%",
              width: "100%",
              height: "100%",
              padding: "0",
              border: "0",
            },
          }}
        >
          <div className={styles.newUserForm}>
            <div
              className={styles.titleWrapper}
              style={{ borderBottom: "1px solid var(--outline-gray)" }}
            >
              <h2 className={styles.title}>
                {getCopy("projects_project_configure")} / {name}
              </h2>

              <div className={styles.buttonsWrapper}>
                <Button
                  role="subtle"
                  onClick={() => {
                    setGroupAdminModal(false);
                  }}
                >
                  {getCopy("studies_search_back")}
                </Button>

                <Button role="primary" onClick={saveChanges}>
                  {getCopy("studies_edit_save-changes")}
                </Button>
              </div>
            </div>

            <div style={{ marginTop: "1rem" }}>
              <div>
                <div style={{ paddingRight: "0.5rem" }}>
                  <h3 className={styles.formTitle}>Group Admin Users</h3>
                  <Table
                    ref={groupAdminModalTableRef}
                    config={{
                      ...groupAdminTableConfig,
                      allowEditing: true,
                      cols: [
                        ...groupAdminTableConfig.cols,
                        {
                          id: "delete",
                          name: "Remove",
                          width: "5%",
                          cellRenderer: deleteCellRenderer,
                        },
                      ],
                    }}
                    data={groupAdmins}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={showGroupMembersModal}
          onRequestClose={() => setGroupMembersModal(false)}
          contentLabel="Group Members"
          style={{
            overlay: {
              backgroundColor: "var(--primary-light)",
              zIndex: 99999,
            },
            content: {
              backgroundColor: "var(--primary-light)",
              position: "absolute",
              top: "0%",
              left: "0%",
              width: "100%",
              height: "100%",
              padding: "0",
              border: "0",
            },
          }}
        >
          <div className={styles.newUserForm}>
            <div
              className={styles.titleWrapper}
              style={{ borderBottom: "1px solid var(--outline-gray)" }}
            >
              <h2 className={styles.title}>
                {getCopy("projects_project_configure")} / {name}
              </h2>

              <div className={styles.buttonsWrapper}>
                <Button
                  role="subtle"
                  onClick={() => {
                    setGroupMembersModal(false);
                  }}
                >
                  {getCopy("studies_search_back")}
                </Button>

                <Button role="primary" onClick={saveChanges}>
                  {getCopy("studies_edit_save-changes")}
                </Button>
              </div>
            </div>

            <div style={{ marginTop: "1rem" }}>
              <div>
                <div style={{ paddingRight: "0.5rem" }}>
                  <h3 className={styles.formTitle}>Group Members</h3>

                  <Table
                    ref={groupMembersModalTableRef}
                    config={{
                      ...groupMembersTableConfig,
                      allowEditing: true,
                      cols: [
                        ...groupMembersTableConfig.cols,
                        {
                          id: "delete",
                          name: "Remove",
                          width: "5%",
                          cellRenderer: deleteCellRenderer,
                        },
                      ],
                    }}
                    data={groupMembers}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </form>
    </div>
  );
}
